import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import API from '../api/axios';
import { toast } from 'react-toastify';
import { FiPlus, FiX, FiTrash2, FiCheck, FiSearch } from 'react-icons/fi';
import './CustomerOrders.css';

function fmtINR(n) { if (!n && n !== 0) return '—'; return '₹' + Number(n).toLocaleString('en-IN'); }
function fmt(iso) { if (!iso) return '—'; return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }

function itemAmount(it) {
  const rate = it.source === 'vendor' ? (it.sellPrice || 0) : (it.rate || 0);
  return (+it.qty || 0) * (+rate || 0);
}

export default function CustomerOrders() {
  const [orders, setOrders]       = useState([]);
  const [selected, setSelected]   = useState(null);
  const [search, setSearch]       = useState('');
  const [loading, setLoading]     = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      const r = await API.get('/customer-orders');
      setOrders(r.data);
      setSelected(prev => prev ? (r.data.find(o => o._id === prev._id) || r.data[0] || null) : (r.data[0] || null));
    } catch { toast.error('Failed to load orders'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = orders.filter(o => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (o.orderNumber || '').toLowerCase().includes(s) || (o.clientName || o.client?.name || '').toLowerCase().includes(s);
  });

  async function toggleItemStatus(order, item) {
    const nextStatus = item.status === 'completed' ? 'pending' : 'completed';
    try {
      const r = await API.put(`/customer-orders/${order._id}/items/${item._id}/status`, { status: nextStatus });
      setOrders(prev => prev.map(o => o._id === r.data._id ? r.data : o));
      setSelected(r.data);
      toast.success(nextStatus === 'completed' ? 'Item marked completed' : 'Item marked pending');
    } catch { toast.error('Failed to update item status'); }
  }

  async function deleteOrder(id) {
    try {
      await API.delete(`/customer-orders/${id}`);
      setConfirmDel(null);
      const next = orders.filter(o => o._id !== id);
      setOrders(next);
      setSelected(next[0] || null);
      toast.success('Order deleted');
    } catch { toast.error('Delete failed'); }
  }

  const orderTotal = (order) => (order.items || []).reduce((s, it) => s + itemAmount(it), 0)
    + (+order.labourCharge || 0) + (+order.installationCharge || 0) + (+order.transportCharge || 0);
  const orderCompletedCount = (order) => (order.items || []).filter(it => it.status === 'completed').length;

  return (
    <Layout>
      <div className="co-root">
        <div className="co-layout">
          {/* LEFT LIST */}
          <div className="co-list-col">
            <div className="co-list-header">
              <span className="co-list-title">All Orders <span className="co-count">{filtered.length}</span></span>
              <button className="co-add-btn" onClick={() => setShowModal(true)}><FiPlus size={13}/> New Order</button>
            </div>
            <div className="co-search-box">
              <FiSearch size={13} className="co-search-icon" />
              <input placeholder="Search orders..." value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <div className="co-list-items">
              {!loading && filtered.length === 0 && <p className="co-list-empty">No orders found</p>}
              {filtered.map(o => (
                <div key={o._id} className={`co-list-item ${selected?._id === o._id ? 'co-item-selected' : ''}`} onClick={() => setSelected(o)}>
                  <div className="co-item-top">
                    <span className="co-item-id">{o.orderNumber}</span>
                    <span className={`co-badge co-badge-${o.status}`}>{o.status === 'completed' ? 'Completed' : 'Pending'}</span>
                  </div>
                  <div className="co-item-client">{o.clientName || o.client?.name || '—'}</div>
                  <div className="co-item-bottom">
                    <span>{orderCompletedCount(o)}/{o.items?.length || 0} items done</span>
                    <span className="co-item-amount">{fmtINR(orderTotal(o))}</span>
                  </div>
                  <div className="co-item-date">{fmt(o.orderDate)}</div>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT DETAIL */}
          <div className="co-detail-col">
            {!selected ? (
              <div className="co-empty-state">
                <p>Select an order to view details</p>
                <button className="co-add-btn" onClick={() => setShowModal(true)}><FiPlus size={13}/> New Order</button>
              </div>
            ) : (
              <div className="co-detail-body">
                <div className="co-detail-header">
                  <div className="co-detail-id-row">
                    <h2>{selected.orderNumber}</h2>
                    <span className={`co-badge co-badge-${selected.status}`}>{selected.status === 'completed' ? 'Completed' : 'Pending'}</span>
                    <button className="co-del-btn" onClick={() => setConfirmDel(selected)}><FiTrash2 size={13}/> Delete</button>
                  </div>
                  <div className="co-meta-strip">
                    <div><span>Customer</span><strong>{selected.clientName || selected.client?.name || '—'}</strong></div>
                    <div><span>Order Date</span><strong>{fmt(selected.orderDate)}</strong></div>
                    <div><span>Total Amount</span><strong className="co-total">{fmtINR(orderTotal(selected))}</strong></div>
                  </div>
                </div>

                <div className="co-table-scroll">
                <table className="co-table">
                  <thead>
                    <tr>
                      <th>#</th><th>Item Name</th><th>Source</th><th>Qty</th>
                      <th>Vendor Cost</th><th>Margin %</th><th>Rate / Sell Price</th><th>Amount</th><th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(selected.items || []).map((it, i) => (
                      <tr key={it._id} className={it.status === 'completed' ? 'co-row-done' : ''}>
                        <td className="co-td-num">{i + 1}</td>
                        <td className="co-td-name">
                          {it.itemName}
                          {it.source === 'vendor' && it.vendorName && <span className="co-vendor-tag"> · {it.vendorName}</span>}
                        </td>
                        <td><span className={`co-src co-src-${it.source}`}>{it.source === 'stock' ? 'From Stock' : 'Vendor + Margin'}</span></td>
                        <td>{it.qty}</td>
                        <td>{it.source === 'vendor' ? fmtINR(it.vendorCost) : '–'}</td>
                        <td>{it.source === 'vendor' ? `${it.marginPercent || 0}%` : '–'}</td>
                        <td>{fmtINR(it.source === 'vendor' ? it.sellPrice : it.rate)}</td>
                        <td>{fmtINR(itemAmount(it))}</td>
                        <td>
                          <button
                            className={`co-status-btn ${it.status === 'completed' ? 'co-status-done' : ''}`}
                            onClick={() => toggleItemStatus(selected, it)}
                          >
                            <FiCheck size={12}/> {it.status === 'completed' ? 'Completed' : 'Mark Complete'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>

                {(selected.labourCharge > 0 || selected.installationCharge > 0 || selected.transportCharge > 0) && (
                  <div className="co-notes-box">
                    <strong>Additional Charges:</strong>{' '}
                    {selected.labourCharge > 0 && <span>Labour: {fmtINR(selected.labourCharge)}  </span>}
                    {selected.installationCharge > 0 && <span>Installation: {fmtINR(selected.installationCharge)}  </span>}
                    {selected.transportCharge > 0 && <span>Transport: {fmtINR(selected.transportCharge)}</span>}
                  </div>
                )}

                {selected.notes && (
                  <div className="co-notes-box"><strong>Notes:</strong> {selected.notes}</div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {showModal && (
        <OrderModal
          onClose={() => setShowModal(false)}
          onSaved={o => { setOrders(prev => [o, ...prev]); setSelected(o); setShowModal(false); toast.success('Order created'); }}
        />
      )}

      {confirmDel && (
        <div className="co-overlay" onClick={() => setConfirmDel(null)}>
          <div className="co-confirm-box" onClick={e => e.stopPropagation()}>
            <h4>Delete Order</h4>
            <p>Delete "{confirmDel.orderNumber}"? This cannot be undone.</p>
            <div className="co-confirm-actions">
              <button className="co-confirm-cancel" onClick={() => setConfirmDel(null)}>Cancel</button>
              <button className="co-confirm-ok" onClick={() => deleteOrder(confirmDel._id)}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

/* ── New Order Modal ── */
function OrderModal({ onClose, onSaved }) {
  const [clients, setClients]     = useState([]);
  const [stockItems, setStockItems] = useState([]);
  const [form, setForm] = useState({ client: '', clientName: '', orderDate: new Date().toISOString().slice(0, 10), notes: '', labourCharge: 0, installationCharge: 0, transportCharge: 0 });
  const [items, setItems] = useState([{ itemName: '', qty: 1, source: 'stock', stockItem: '', rate: 0, vendorName: '', vendorCost: 0, marginPercent: 0 }]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    API.get('/clients').then(r => setClients(r.data)).catch(() => {});
    API.get('/stock-items').then(r => setStockItems(r.data)).catch(() => {});
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const addItem = () => setItems(p => [...p, { itemName: '', qty: 1, source: 'stock', stockItem: '', rate: 0, vendorName: '', vendorCost: 0, marginPercent: 0 }]);
  const removeItem = i => setItems(p => p.filter((_, idx) => idx !== i));
  const setItemField = (i, k, v) => setItems(p => p.map((it, idx) => idx === i ? { ...it, [k]: v } : it));

  function onClientChange(clientId) {
    const found = clients.find(c => c._id === clientId);
    set('client', clientId);
    set('clientName', found ? found.name : '');
  }

  function onStockPick(i, stockId) {
    const stock = stockItems.find(s => s._id === stockId);
    setItems(p => p.map((it, idx) => idx !== i ? it : {
      ...it, stockItem: stockId, itemName: stock?.itemName || it.itemName, rate: stock?.rate || 0,
    }));
  }

  function onSourceChange(i, source) {
    setItems(p => p.map((it, idx) => idx !== i ? it : { ...it, source, stockItem: '', itemName: source === 'vendor' ? it.itemName : '' }));
  }

  async function save() {
    if (!form.clientName.trim()) return toast.error('Customer is required');
    if (!items.length || items.some(it => !it.itemName.trim())) return toast.error('Every item needs a name');
    for (const it of items) {
      if (it.source === 'stock' && it.stockItem) {
        const stock = stockItems.find(s => s._id === it.stockItem);
        if (stock && (+it.qty || 0) > stock.qty) {
          return toast.error(`"${stock.itemName}" has only ${stock.qty} in stock — cannot order ${it.qty}`);
        }
      }
    }
    setSaving(true);
    try {
      const cleanedItems = items.map(it => ({ ...it, stockItem: it.stockItem || undefined }));
      const payload = { ...form, items: cleanedItems };
      const r = await API.post('/customer-orders', payload);
      onSaved(r.data);
    } catch (e) { toast.error(e.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  }

  return (
    <div className="co-overlay">
      <div className="co-modal" onClick={e => e.stopPropagation()}>
        <div className="co-modal-header">
          <h3>New Customer Order</h3>
          <button className="co-modal-close" onClick={onClose}><FiX /></button>
        </div>
        <div className="co-modal-body">
          <div className="co-mrow">
            <div className="co-mgroup">
              <label>Customer *</label>
              <select value={form.client} onChange={e => onClientChange(e.target.value)}>
                <option value="">Select Customer...</option>
                {clients.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
              </select>
            </div>
            <div className="co-mgroup">
              <label>Order Date</label>
              <input type="date" value={form.orderDate} onChange={e => set('orderDate', e.target.value)} />
            </div>
          </div>

          <div className="co-items-editor">
            <div className="co-items-editor-header">
              <span>Order Items</span>
              <button className="co-btn-ghost" onClick={addItem}><FiPlus size={11}/> Add Item</button>
            </div>
            {items.map((it, i) => (
              <div key={i} className="co-item-row-editor">
                <div className="co-mrow">
                  <div className="co-mgroup">
                    <label>Source</label>
                    <select value={it.source} onChange={e => onSourceChange(i, e.target.value)}>
                      <option value="stock">From Stock</option>
                      <option value="vendor">Buy from Vendor (+ Margin)</option>
                    </select>
                  </div>
                  {it.source === 'stock' ? (
                    <div className="co-mgroup co-mfull">
                      <label>Stock Item</label>
                      <select value={it.stockItem} onChange={e => onStockPick(i, e.target.value)}>
                        <option value="">Select stock item...</option>
                        {stockItems.map(s => <option key={s._id} value={s._id}>{s.itemName} (Qty: {s.qty})</option>)}
                      </select>
                    </div>
                  ) : (
                    <div className="co-mgroup co-mfull">
                      <label>Item Name</label>
                      <input value={it.itemName} onChange={e => setItemField(i, 'itemName', e.target.value)} placeholder="Item name" />
                    </div>
                  )}
                  <button className="co-remove-item" onClick={() => removeItem(i)}><FiX size={11}/></button>
                </div>
                {it.source === 'vendor' && (
                  <div className="co-mrow">
                    <div className="co-mgroup co-mfull">
                      <label>Vendor Name</label>
                      <input value={it.vendorName} onChange={e => setItemField(i, 'vendorName', e.target.value)} placeholder="e.g. ABC Traders" />
                    </div>
                  </div>
                )}
                <div className="co-mrow">
                  <div className="co-mgroup">
                    <label>Qty{it.source === 'stock' && it.stockItem ? ` (max ${stockItems.find(s => s._id === it.stockItem)?.qty ?? 0})` : ''}</label>
                    <input
                      type="number" min="1"
                      max={it.source === 'stock' && it.stockItem ? stockItems.find(s => s._id === it.stockItem)?.qty : undefined}
                      value={it.qty}
                      onChange={e => setItemField(i, 'qty', +e.target.value)}
                    />
                  </div>
                  {it.source === 'stock' ? (
                    <div className="co-mgroup">
                      <label>Rate (₹)</label>
                      <input type="number" min="0" value={it.rate} onChange={e => setItemField(i, 'rate', +e.target.value)} />
                    </div>
                  ) : (
                    <>
                      <div className="co-mgroup">
                        <label>Vendor Cost (₹)</label>
                        <input type="number" min="0" value={it.vendorCost} onChange={e => setItemField(i, 'vendorCost', +e.target.value)} />
                      </div>
                      <div className="co-mgroup">
                        <label>Margin %</label>
                        <input type="number" min="0" value={it.marginPercent} onChange={e => setItemField(i, 'marginPercent', +e.target.value)} />
                      </div>
                      <div className="co-mgroup">
                        <label>Sell Price (₹)</label>
                        <input value={fmtINR((+it.vendorCost || 0) * (1 + (+it.marginPercent || 0) / 100))} disabled />
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="co-mrow">
            <div className="co-mgroup">
              <label>Labour Charge (₹)</label>
              <input type="number" min="0" value={form.labourCharge} onChange={e => set('labourCharge', +e.target.value)} />
            </div>
            <div className="co-mgroup">
              <label>Installation Charge (₹)</label>
              <input type="number" min="0" value={form.installationCharge} onChange={e => set('installationCharge', +e.target.value)} />
            </div>
            <div className="co-mgroup">
              <label>Transport Charge (₹)</label>
              <input type="number" min="0" value={form.transportCharge} onChange={e => set('transportCharge', +e.target.value)} />
            </div>
          </div>

          <div className="co-mgroup">
            <label>Notes</label>
            <textarea rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} />
          </div>
        </div>
        <div className="co-modal-footer">
          <button className="co-btn-ghost" onClick={onClose}>Cancel</button>
          <button className="co-btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Create Order'}</button>
        </div>
      </div>
    </div>
  );
}
